import type { EmpyreanEngineRun, EmpyreanRunKind } from './empyrean-engine-run';

export interface EmpyreanDataDefinition {
  id: number;
  dataType: string;
  numericalOrder: number | null;
  category: string | null;
  dataFieldName: string | null;
  dataFieldReference: string | null;
  definition: string | null;
  fieldDataType: string | null;
  fieldNumber: number | null;
  sortOrder: number;
  /** When true, a blank/null output value in this field blocks Empyrean file generation. */
  isRequired: boolean;
}

export interface EmpyreanAlmAccount {
  /** identity of the chart-of-accounts row (needed to edit/delete a parent) */
  id: number;
  /**
   * ⚠ **Unique only WITH {@link modelType}.** 94 of the 276 CU ALM account numbers also exist in
   * the IRRM chart and 34 of those carry a different name or parent — `1003700` is *Total CMBS
   * (AFS)* under `12230000` for CU and *CMBS (AFS)* under `1002500` for IRRM. A lookup on the
   * number alone returns whichever row the engine happens to hand back first.
   */
  almAccountNum: string;
  accountName: string;
  /** which chart this row belongs to; see {@link EmpyreanModelType} */
  modelType: EmpyreanModelType;
  displayOrder: number | null;
  isActive: boolean;
}

export const empyreanConversionTypes = [
  'DATE_FORMAT',
  'VALUE_MAP',
  'ADD_MONTHS_TO_DATE',
  'ARITHMETIC',
  'ZONED_DECIMAL',
  'TERM_TO_MONTHS',
  'VARIABLE_ONLY'
] as const;
export type EmpyreanConversionType = (typeof empyreanConversionTypes)[number];

export interface EmpyreanDateFormatConfig {
  /** date-fns format the source string is parsed with; omitted = auto-detect (Excel date/serial or ISO) */
  inputFormat?: string;
  outputFormat: string;
}

export interface EmpyreanValueMapConfig {
  mappings: { from: string; to: string }[];
  /** unmatched values pass through unchanged when true, otherwise become blank */
  passThroughUnmatched: boolean;
  /**
   * file column (same row) whose value the map matches `from` against, instead of this cell's own — a term
   * set per Loan Type Code (PROD-2778). Omitted: the cell's own value, as before.
   */
  keyColumn?: string;
  /**
   * apply only when this cell is blank or 0; a value the core sent ships as it came (PROD-2778). Omitted or
   * false: every row is mapped, as before.
   */
  onlyWhenBlank?: boolean;
}

export interface EmpyreanAddMonthsToDateConfig {
  /** file column (same row) holding the base date the numeric cell value is added to, in months */
  dateColumn: string;
  outputFormat: string;
  /**
   * PROD-2644: a reset date that must land after the As Of. Empyrean's NextResetDate is the next reset AFTER the
   * position date, and Origination + First Reset is in the past for a loan already beyond its first reset. When the
   * computed date is on or before the As Of it steps forward by `monthsColumn`'s periodic months until it is after it.
   * A blank, 0 or unreadable period leaves the date as computed; a blank cell value gives no date at all.
   */
  rollForward?: {
    /** file column (same row) holding the periodic reset, in months — CU ALM's `Periodic Reset (mos)` */
    monthsColumn: string;
  };
}

export interface EmpyreanArithmeticConfig {
  operation: 'ADD' | 'SUBTRACT' | 'MULTIPLY' | 'DIVIDE';
  /** constant applied to the cell's numeric value; result is full precision (no rounding) */
  operand: number;
}

/**
 * COBOL zoned-decimal ("overpunch") numerics, as written by mainframe cores. The LAST character
 * of the field carries both the final digit and the sign — `{ABCDEFGHI` = +0..+9 and
 * `}JKLMNOPQR` = −0..−9 — so `0000076876B` is +7687.62 at scale 2. Because a trailing letter is
 * ambiguous with an ordinary alphanumeric code, this is opt-in per column rather than something
 * the generic numeric parse guesses at.
 */
export interface EmpyreanZonedDecimalConfig {
  /** implied decimal places; 2 reads 0000076876B as 7687.62, 3 reads 0700{ as 7.000 */
  scale: number;
  /**
   * Optional file column (same row) holding a separate sign flag, for cores that keep the sign
   * outside the field. Needed by McCoy Meri, whose loan records carry `7` in the position before
   * the balance to mean a credit balance.
   */
  signColumn?: string;
  /** values of signColumn that mean the amount is negative; anything else is positive */
  negativeValues?: string[];
}

/**
 * A term whose unit is named in another column of the same row, written out in months — Empyrean's
 * `OriginalTerm` (PROD-1646). Fiserv Premier's CD files give `Term` in months OR days, by `Term Code`
 * (5KIR: `M` 9–60, `D` 91 / 182), and a split or value map can only match the raw number.
 *
 * A unit in none of the lists becomes blank, never a guess: a day count read as months is a
 * 91-month CD.
 */
export interface EmpyreanTermToMonthsConfig {
  /** file column (same row) naming the term's unit, e.g. Premier's `Term Code` */
  unitColumn: string;
  /** unit values meaning the term is already in months (`M`) */
  monthValues: string[];
  /** unit values meaning days (`D`): rounded to whole months, 91 → 3 and 182 → 6 */
  dayValues: string[];
  /** unit values meaning years (`Y`), times 12 */
  yearValues?: string[];
}

/**
 * A number written on a VARIABLE row only, blank on a fixed one — Empyrean's `ResetMths` and `ResetSpread`
 * off a Fiserv Precision loan record (PROD-1877). Precision writes reset months `00` on a Prime floater AND
 * on every fixed loan, so a value map cannot tell them apart: the row's rate index can. `00` on a floater
 * ships as 0, which Empyrean reads as floating with its index (UG p.180).
 *
 * A row is variable when its index names one (not blank, not a `noIndexValues` entry), or when its account
 * is Variable (Emport 98's Fixed / Variable, `resolveLoanChildRateType`) whatever the file's index says.
 */
export interface EmpyreanVariableOnlyConfig {
  /** file column (same row) holding the rate index, e.g. Precision's `Index Code` */
  indexColumn: string;
  /** index values meaning no index, i.e. fixed (`00`); a blank index is always none */
  noIndexValues: string[];
  /** the number is divided by this before it ships (100 for a percent spread); absent = as read */
  divisor?: number;
}

export type EmpyreanConversionRule =
  | ({ type: 'DATE_FORMAT' } & EmpyreanDateFormatConfig)
  | ({ type: 'VALUE_MAP' } & EmpyreanValueMapConfig)
  | ({ type: 'ADD_MONTHS_TO_DATE' } & EmpyreanAddMonthsToDateConfig)
  | ({ type: 'ARITHMETIC' } & EmpyreanArithmeticConfig)
  | ({ type: 'ZONED_DECIMAL' } & EmpyreanZonedDecimalConfig)
  | ({ type: 'TERM_TO_MONTHS' } & EmpyreanTermToMonthsConfig)
  | ({ type: 'VARIABLE_ONLY' } & EmpyreanVariableOnlyConfig);

export const empyreanFileFormats = ['EXCEL', 'DELIMITED', 'FIXED_WIDTH'] as const;
export type EmpyreanFileFormat = (typeof empyreanFileFormats)[number];

export const empyreanRowFilterOperators = [
  'EQUALS',
  'NOT_EQUALS',
  'STARTS_WITH',
  'NOT_STARTS_WITH',
  'LESS_THAN',
  'LESS_OR_EQUAL',
  'GREATER_THAN',
  'GREATER_OR_EQUAL',
  'BETWEEN',
  'BLANK',
  'NOT_BLANK'
] as const;
export type EmpyreanRowFilterOperator = (typeof empyreanRowFilterOperators)[number];

/** Reserved `column` token: test the raw pre-parse line's character length (text formats only). */
export const EMPYREAN_LINE_LENGTH_COLUMN = 'LINE_LENGTH';

/**
 * Reserved column holding the name of the file a row came from. Stamped on every parsed row, and
 * surfaced as a real column only when several files are merged into one Data Type — which is the
 * case it exists for. Cores that split one instrument set across several files put the
 * distinguishing attribute in the FILENAME (CSI Detail's four loan files are commercial /
 * installment / mortgage / revolving), so without this the merge would silently collapse them.
 * Usable by row filters and split rules like any other column.
 */
export const EMPYREAN_SOURCE_FILE_COLUMN = 'SOURCE_FILE';

/**
 * A Data Type Variant: one of several INDEPENDENT record layouts a single Data Type is shipped in.
 *
 * Needed by cores that split one product set across several files whose layouts differ — CSI
 * Detail ships four loan files and two deposit files, all 180 characters, sharing only a 30-char
 * prefix. Merging their rows (see {@link EMPYREAN_SOURCE_FILE_COLUMN}) is not enough, because their
 * class codes COLLIDE with different meanings: a commercial class `2` is not a mortgage class `2`.
 * So each variant needs its own file setting, its own account-code mappings and its own split
 * rules — which is why `Variant` joins `(ProcessorType, PortfolioCode, DataType)` on those three
 * tables and nothing else.
 *
 * What deliberately does NOT vary by variant: the output worksheet (every variant of Loans merges
 * into ONE sheet), the Empyrean field set (Loans is Loans), and generated child ALM account numbers
 * (allocated per (ProcessorType, PortfolioCode) across everything, so variants can't collide).
 *
 * `null` is THE DEFAULT — the single unvarianted layout, which is what every already-configured
 * bank uses. `''` means the same thing and is normalized to `null` on the way in, so a blank input
 * and an absent one behave identically.
 */
export const EMPYREAN_DEFAULT_VARIANT = null;

/** Normalizes a variant from any surface: blank/whitespace/absent all mean the default (null). */
export const normalizeEmpyreanVariant = (variant: string | null | undefined): string | null =>
  variant?.trim() ? variant.trim() : null;

/**
 * Stable identity of one loaded dataset. A Data Type alone is no longer unique once variants
 * exist, so every dataset lookup keys on this instead. The default variant keys as the bare Data
 * Type, so every key in an unvarianted workspace is unchanged.
 *
 * JSON rather than a joined string on purpose: variant is free text and both parts can contain
 * spaces, so any literal separator risks a collision — `('Term', 'Deposits')` must never key the
 * same as `('Term Deposits', null)`. JSON escaping makes that impossible.
 */
export const empyreanDatasetKey = (dataType: string, variant: string | null | undefined): string => {
  const normalized = normalizeEmpyreanVariant(variant);
  return normalized ? JSON.stringify([dataType, normalized]) : dataType;
};

/** Human-readable dataset label — "Loans" for the default, "Loans · Mortgage" for a variant. */
export const empyreanDatasetLabel = (dataType: string, variant: string | null | undefined): string => {
  const normalized = normalizeEmpyreanVariant(variant);
  return normalized ? `${dataType} · ${normalized}` : dataType;
};

/** The non-maturity deposits Data Type the savings types were split out of (PROD-458). */
export const EMPYREAN_NMD_DATA_TYPE = 'NMD';

/**
 * PROD-458 (Luke, 5 Oct 2026): Savings and IRA Savings files stage as their own Data Types, each its own
 * worksheet, but they ARE non-maturity deposits — NMD's 61 fields, its no-maturity rule, its
 * Non-Maturity classification. Luke routes by account code, never by an "IRA" label.
 */
export const EMPYREAN_SAVINGS_DATA_TYPES: readonly string[] = ['Savings', 'IRA Savings'];

export const isEmpyreanSavingsDataType = (dataType: string | null | undefined): boolean =>
  !!dataType && EMPYREAN_SAVINGS_DATA_TYPES.includes(dataType);

/** NMD and the two Data Types split out of it — the non-maturity deposits family. */
export const isEmpyreanNonMaturityDepositDataType = (dataType: string | null | undefined): boolean =>
  dataType === EMPYREAN_NMD_DATA_TYPE || isEmpyreanSavingsDataType(dataType);

/**
 * A (Data Type, Variant) pair a client already has a saved layout for. Never carries the default
 * variant: `null` needs no offering, since leaving the Variant box empty already means it.
 *
 * Descriptive, not authoritative — it lists what exists, not what is permitted. A variant has to be
 * typeable before its layout can be saved, or a new processor could never be configured at all.
 */
export interface EmpyreanDataTypeVariant {
  dataType: string;
  variant: string;
}

/** One content-based row filter. Multiple filters AND together. */
export interface EmpyreanRowFilter {
  /** a file column name, OR the reserved token EMPYREAN_LINE_LENGTH_COLUMN */
  column: string;
  operator: EmpyreanRowFilterOperator;
  /**
   * comparison value; ignored for BLANK/NOT_BLANK. Both sides are trimmed.
   * EQUALS / NOT_EQUALS compare numbers as numbers and text as text (`coreValuesEqual` in
   * empyrean-import): when both sides read as numbers they compare by exact value, so `= 0`
   * matches `0.00` and `0.00-`, and `= 001` matches `1`; otherwise it is a case-sensitive
   * text compare. A blank cell is never a number.
   * The range operators (LESS_THAN … BETWEEN) read the cell with `parseCoreNumber` and this
   * value with Number(); a cell that isn't a number never matches them.
   * STARTS_WITH / NOT_STARTS_WITH always compare as case-sensitive text. They exist for files
   * whose data type is encoded as a prefix of a composite key (e.g. Shazam's `DDA-139-V-BI3`
   * vs `LN-LNS-CRE-3-L04`), where one raw file feeds several DataTypes. Since filters AND
   * together, a set of NOT_STARTS_WITH filters expresses "any of these prefixes" by excluding
   * the others.
   */
  value: string | null;
  /** upper bound for BETWEEN only; null otherwise */
  value2: string | null;
  /** KEEP = keep only rows that match; DROP = remove rows that match */
  action: 'KEEP' | 'DROP';
}

/** Which file cells {@link EmpyreanCodeDefaults} counts as missing: an empty one, or one reading as 0. */
export const empyreanCodeDefaultsWhen = ['blank', 'zero'] as const;
export type EmpyreanCodeDefaultsWhen = (typeof empyreanCodeDefaultsWhen)[number];

/** One file column's default for one code. */
export interface EmpyreanCodeDefaultValue {
  /** a file column name */
  column: string;
  /** the value written into a missing cell, as the client would have sent it; trimmed */
  value: string;
}

/** One code's defaults, one entry per file column it fills. */
export interface EmpyreanCodeDefaultsRow {
  /** the key column's value this row applies to; compared numbers-as-numbers (`59` = `059`) */
  key: string;
  values: EmpyreanCodeDefaultValue[];
}

/** Extra values that count as missing in one column, beside blank / zero (an Original Term of 1). */
export interface EmpyreanCodeMissingValues {
  column: string;
  values: string[];
}

/**
 * A per-code defaults table on a layout (PROD-2641): fills a missing FILE CELL from its row's code,
 * after the row filters and before splits and field mapping. The layout's mappings then convert
 * the filled value exactly as if the client had sent it, so one table carries both a credit union's
 * variable-rate terms by loan type and its default term per loan type.
 *
 * Only a missing cell on a row whose key is listed is filled. A real value is never overwritten, and
 * an unlisted key stays as the file has it.
 *
 * ⚠ The values are a list of pairs rather than a `{ [column]: value }` map so the table travels over
 * GraphQL typed, the way the row filters do; a map would need a JSON scalar the schema doesn't have.
 */
export interface EmpyreanCodeDefaults {
  /** the file column whose value picks the row's defaults (e.g. `Loan Type Code`) */
  keyColumn: string;
  /** what counts as missing; empty = nothing is ever filled */
  when: EmpyreanCodeDefaultsWhen[];
  /** per column, more values treated as missing; empty = blank / zero only */
  missingValues: EmpyreanCodeMissingValues[];
  rows: EmpyreanCodeDefaultsRow[];
}

export interface EmpyreanFileSettingColumn {
  columnName: string;
  sortOrder: number;
  dataFieldReference: string | null;
  conversionType: string | null;
  /** JSON-serialized config matching conversionType (see EmpyreanConversionRule) */
  conversionConfig: string | null;
  /** 1-based character start for FIXED_WIDTH layout columns; omitted/null otherwise */
  startPosition?: number | null;
  /** field width for FIXED_WIDTH; null/blank on the last layout column = rest of line */
  length?: number | null;
}

export interface EmpyreanFileSetting {
  processorType: string;
  /** Portfolio (bank) the layout belongs to; settings are per ProcessorType + PortfolioCode + DataType + Variant */
  portfolioCode: string | null;
  dataType: string;
  /**
   * One of the Data Type's independent record layouts; null = the default (single) layout.
   * See {@link EMPYREAN_DEFAULT_VARIANT}.
   */
  variant: string | null;
  /** EXCEL | DELIMITED | FIXED_WIDTH */
  fileFormat: EmpyreanFileFormat;
  /** literal delimiter chars for DELIMITED (tab stored as the literal tab char); null otherwise */
  delimiter: string | null;
  /** whether the file has a header row (EXCEL/DELIMITED); FIXED_WIDTH is always header-less */
  hasHeaderRow: boolean;
  /**
   * DELIMITED only. Some cores wrap each whole RECORD in one pair of quotes
   * (`"a,b,c"` rather than `"a","b","c"`), which a CSV parser reads as a single field. When set,
   * a wrapping quote pair is stripped from any physical line that is entirely wrapped, before
   * the line is split. Lines that aren't wrapped are left alone, so enabling this on a shared
   * processor default is safe for the banks that don't need it. Needed by JH Silverlake / QUAL.
   */
  unwrapQuotedLines: boolean;
  /** junk/preamble rows to drop from the top of a text file; null = 0 */
  skipFirstLines: number | null;
  /** junk/footer rows to drop from the bottom of a text file; null = 0 */
  skipLastLines: number | null;
  /** content-based row filters (see EmpyreanRowFilter); empty = none */
  rowFilters: EmpyreanRowFilter[];
  accountCodeColumn: string | null;
  /** File column whose numeric values the COA Summary tab totals; null = no summary totals. */
  balanceColumn: string | null;
  /** Worksheet/tab name to read for this DataType; null = use the first worksheet. */
  tabName: string | null;
  /**
   * Worksheet name this DataType writes to in the generated Empyrean file; null = the DataType's own
   * name. Set on a processor default where the engine expects different tab names (CU ALM wants
   * Loans on a "Loan Data" tab), with the usual per-portfolio override available.
   */
  outputTabName: string | null;
  /**
   * File column that carries each account code's NAME (PROD-2471): Account Codes shows it beside the
   * code so a first-time mapping never leaves the step. Optional; null = nothing shown. Read-only
   * context — it never reaches a generated file.
   */
  codeNameColumn?: string | null;
  /**
   * PROD-2641: per-code defaults for missing file cells, applied at Load right after the row filters;
   * null / absent = none. Optional so a layout saved before it existed reads exactly as it did.
   */
  codeDefaults?: EmpyreanCodeDefaults | null;
  columns: EmpyreanFileSettingColumn[];
  /**
   * PROD-458: set when this is NOT a saved layout of the asked-for Data Type but the client's own NMD
   * layout, offered for Savings / IRA Savings until the client saves one of its own — "copy on first
   * use" (Luke, 5 Oct 2026). Absent or null on every saved layout. A read-only answer: saving writes the
   * asked-for Data Type's row, never NMD's.
   */
  copiedFromDataType?: string | null;
}

export interface EmpyreanCategoryAlmAccount {
  /**
   * Which chart {@link EmpyreanCategoryAlmAccount.almAccountNum} belongs to.
   *
   * ⚠ Required, not optional: a category pointing at a bare account number is ambiguous across the
   * 94 numbers the two charts share.
   */
  modelType: EmpyreanModelType;
  category: string;
  subCategory: string | null;
  almAccountNum: string;
}

/**
 * One Data Type a governed parent (Total) account may serve — the tag that scopes the Account
 * Codes tab's Parent Account picker, so a Loans tab offers loan parents rather than all 105.
 *
 * ⭐ A JUNCTION, not a column on the chart. Measured on dev: two parents already serve two Data
 * Types each (`2005000` Total Repos and `2006400` Total IRA Savings are both NMD and Term
 * Deposits), so a single `DataType` column could not express what analysts are already doing.
 *
 * ⚠⚠ **The key carries the MODEL TYPE.** 18 `Total` account numbers exist on BOTH charts, so
 * `(DataType, ALMAccountNum)` alone cannot say which parent it means — the same trap that made the
 * category map model-type-scoped in #406.
 *
 * ⚠ It SCOPES the picker; it never hard-filters it. A Data Type nobody has tagged must read as
 * "nothing has been narrowed here yet", never as "no parents exist" — hence the Account Codes
 * tab's "Show all parents" fallback.
 */
export interface EmpyreanAlmAccountDataType {
  modelType: EmpyreanModelType;
  dataType: string;
  almAccountNum: string;
}

/**
 * Which institutions a governed parent (Total) account serves — Bank, CU or both (PROD-1546). The
 * sibling of {@link EmpyreanAlmAccountDataType}, and the same shape for the same reason: one row per
 * tag, so no rows = untagged, one row = Bank-only or CU-only, two rows = both.
 *
 * ⭐ IRRM uses different parents for banks and credit unions, so an IRRM bank mapping a Term
 * Deposits file should see only the parents tagged Bank and Term Deposits. **An untagged parent is
 * offered to everyone** (Mike, 2026-10-02): nothing is tagged on day one, and hiding untagged
 * parents would empty every IRRM client's picker until all of them were.
 *
 * ⚠ **IRRM only.** The CU ALM chart is credit-union-only, so the picker ignores these tags there
 * (Mike, 2026-10-02) and the Parent Accounts modal does not offer them.
 */
export interface EmpyreanAlmAccountInstitutionKind {
  modelType: EmpyreanModelType;
  institutionKind: EmpyreanInstitutionKind;
  almAccountNum: string;
}

export interface EmpyreanAccountCodeMapping {
  processorType: string;
  portfolioCode: string;
  dataType: string;
  /**
   * The Data Type variant this code belongs to; null = the default layout. Mappings are keyed per
   * variant because variant account codes collide with DIFFERENT meanings — CSI Detail's commercial
   * class `2` is not its mortgage class `2`.
   */
  variant: string | null;
  accountCode: string;
  description: string | null;
  category: string | null;
  subCategory: string | null;
  /** the parent (Total) ALM account this code rolls up to; chosen from the global parent list */
  parentAlmAccountNum: string | null;
  /**
   * the client-specific child ALM account number emitted to the BRD file. Generated
   * server-side (parent + next free offset in the parent's band) and sticky once assigned.
   */
  almAccountNum: string | null;
  /**
   * PROD-1727: the analyst's Fixed / Variable CHOICE for the loan child this code mints; null = no
   * choice, so the child takes its default (`resolveLoanChildRateType`: the parent, else the name).
   *
   * Optional, and absent reads like null — except on a SAVE, where an absent field keeps the stored
   * choice and only an explicit null clears it, so a caller that predates the field cannot wipe one.
   */
  rateType?: EmpyreanLoanRateType | null;
  /**
   * PROD-458, a save's answer only: this code's link was MOVED here from that Data Type (NMD) by this
   * save, because a code linked under both would load its balances twice. Never stored.
   */
  movedFromDataType?: string | null;
}

/**
 * A client loan child's Fixed / Variable choice (PROD-1727) — Luke, 5 Oct 2026: "Every loan account must
 * be either Fixed or Variable". Two values, not the chart's three: a loan child is never Non-interest by
 * choice (one beneath a non-accrual Total takes that from its parent).
 */
export const empyreanLoanRateTypes = ['Fixed', 'Variable'] as const;
export type EmpyreanLoanRateType = (typeof empyreanLoanRateTypes)[number];

export const empyreanSplitColumnTypes = ['NUMERIC', 'DATE', 'STRING'] as const;
export type EmpyreanSplitColumnType = (typeof empyreanSplitColumnTypes)[number];

/** Inclusive numeric range; either bound null = open-ended on that side. */
export interface EmpyreanNumericRangeConfig {
  min: number | null;
  max: number | null;
}

/** Inclusive date range (yyyy-MM-dd strings); either bound null = open-ended on that side. */
export interface EmpyreanDateRangeConfig {
  start: string | null;
  end: string | null;
}

/** Set of distinct string values that belong to the group. */
export interface EmpyreanStringSetConfig {
  members: string[];
}

/**
 * One group/range within a split-rule column. Real groups carry a `config` (JSON matching
 * the column's type — see EmpyreanNumericRangeConfig / EmpyreanDateRangeConfig /
 * EmpyreanStringSetConfig). The single catch-all group (`isCatchAll`) has a null config and
 * labels every value that matches no real group.
 */
export interface EmpyreanSplitRuleGroup {
  splitLabel: string;
  sortOrder: number;
  isCatchAll: boolean;
  config: string | null;
  /**
   * Stable identity for this group, minted client-side and preserved across edits. A sub-split
   * hangs off a group key rather than its label, so relabelling `ARM` to `Adjustable` does not
   * orphan everything beneath it — and it survives saveSplitRule deleting and re-inserting the
   * rule's columns and groups, which reassigns every database-generated id.
   *
   * Null on groups saved before sub-splits existed. Such a group simply cannot have children.
   */
  groupKey: string | null;
}

export interface EmpyreanSplitRuleColumn {
  columnName: string;
  columnType: EmpyreanSplitColumnType;
  sortOrder: number;
  groups: EmpyreanSplitRuleGroup[];
}

/**
 * The `accountCode` of a *generic* split rule — one that splits every line item regardless of its
 * account code (e.g. the CU ALM coupon-type split into FR / VR / ARM). Stored as NULL in
 * emp.EmpyreanAccountSplitRules.AccountCode and carried over the wire as this empty string, which
 * is unambiguous because a row with a blank account code is never assigned a code at all.
 *
 * A dataset has at most one generic rule; its labels are applied *before* any account-code-specific
 * rule's, so a code ends up as `{code}-{generic labels} {specific labels}`.
 */
export const EMPYREAN_GENERIC_SPLIT_ACCOUNT_CODE = '';

/**
 * Separator for the group keys in {@link EmpyreanSplitRule.parentGroupPath}. Group keys are minted
 * from a restricted alphabet that excludes it, so a path is unambiguous to split.
 */
export const EMPYREAN_SPLIT_PATH_SEPARATOR = '|';

/**
 * A branch's group keys in CANONICAL order — sorted, so the path identifies the branch and nothing
 * else.
 *
 * ⭐⭐ **The order a level's labels are displayed in must not decide what its sub-splits hang off,
 * and before this it did.** The keys used to be stored in resolution order, which is
 * `orderColumnsWithGeneric(own, generic, genericColumnsPosition)` — the same order that builds the
 * label suffix. So Move up / Move down, or moving the generic entry, rewrote the branch identity of
 * a level the analyst only meant to re-*display*: a sub-rule saved under `gKey|ownKey` stopped
 * matching `ownKey|gKey`, its rows quietly stopped descending, and — because the carry-forward
 * judges a reorder a pure relabel (see `isRelabelOnly`) — the mappings were renamed as if nothing
 * had broken. Next month's file simply stopped splitting that bucket, with nothing on screen to
 * say so. PROD-486.
 *
 * Sorting is what decouples them: a group key is already a stable minted identity, so the SET of
 * keys names the branch completely and their order carries no information at all. Display order is
 * then free to change.
 *
 * ⚠ **Sorted by code unit (plain `Array.sort`), deliberately — never by locale.** Keys are
 * `g` + base-36, so this is ordinal over `[0-9a-z]`, which `canonicalize-split-parent-group-paths.sql`
 * reproduces exactly under a `_BIN2` collation. A locale compare would put the two sides of the
 * estate on different orders and re-create the bug it fixes.
 *
 * ⚠ Rules saved BEFORE this change hold their keys in resolution order. Reading is tolerant —
 * every comparison canonicalizes both sides — but a WRITE must be canonical, and the SQL script
 * above canonicalizes what is already stored so the server's own `findOne` by path keeps matching.
 */
export const canonicalizeEmpyreanSplitPath = (path: string | null | undefined): string | null => {
  if (!path) {
    return null;
  }
  const keys = path.split(EMPYREAN_SPLIT_PATH_SEPARATOR).filter((key) => !!key);
  return keys.length ? [...keys].sort().join(EMPYREAN_SPLIT_PATH_SEPARATOR) : null;
};

/** Encodes matched group keys as a {@link EmpyreanSplitRule.parentGroupPath} (null at the root). */
export const encodeEmpyreanSplitPath = (groupKeys: string[]): string | null =>
  canonicalizeEmpyreanSplitPath(groupKeys.join(EMPYREAN_SPLIT_PATH_SEPARATOR));

/**
 * True when two paths name the same branch, whatever order either was written in. Use this in place
 * of `===` everywhere a stored path meets a computed one — a legacy row is only equal after both
 * sides are canonicalized.
 */
export const empyreanSplitPathsMatch = (left: string | null | undefined, right: string | null | undefined): boolean =>
  canonicalizeEmpyreanSplitPath(left) === canonicalizeEmpyreanSplitPath(right);

/**
 * How many rules deep one account code may be split — the broad bucket plus three refinements.
 * Resolution already terminates on its own (each level consumes a strictly longer group path), so
 * this is a bound on analyst-facing complexity rather than a loop guard.
 */
export const EMPYREAN_MAX_SPLIT_DEPTH = 4;

export interface EmpyreanSplitRule {
  processorType: string;
  portfolioCode: string;
  dataType: string;
  /** The Data Type variant whose account code this splits; null = the default layout. */
  variant: string | null;
  /** the pre-split code this rule splits, or {@link EMPYREAN_GENERIC_SPLIT_ACCOUNT_CODE} for all codes */
  accountCode: string;
  /**
   * The group keys a row must already have matched for this rule to apply, joined by
   * {@link EMPYREAN_SPLIT_PATH_SEPARATOR}; null = the top level.
   *
   * This is what makes an ASYMMETRIC breakdown possible: the ARM bucket can be split by reset index
   * while the FR bucket is split by term, or not split at all, because each is a separate rule
   * hanging off its own group. A path names one branch and only that branch, so a rule only ever
   * fires where it was defined.
   *
   * ⭐ **Written in CANONICAL (sorted) order** — see {@link canonicalizeEmpyreanSplitPath}, which
   * also explains what went wrong while the order was the level's display order. Compare with
   * {@link empyreanSplitPathsMatch}, never `===`: rows saved before that change still hold their
   * keys in resolution order until `canonicalize-split-parent-group-paths.sql` has run.
   */
  parentGroupPath: string | null;
  /**
   * Where the *generic* rule's columns land among THIS rule's own — an index into this rule's own
   * (sorted) column list, so `0` puts the generic labels first and `columns.length` puts them last.
   *
   * ⭐ **Null means first**, which is what every rule saved before PROD-486 meant, so an existing
   * client's split codes stay byte-identical until an analyst actually moves the generic entry.
   * Meaningless on the generic rule itself (it has no generic columns to place) and on a code with
   * no generic rule at its level; null in both cases.
   *
   * ⚠ Written EXPLICITLY at save time and never re-derived. Every saved Account Code Mapping is
   * keyed on the resulting code, so a position inferred from whichever rules happen to exist at
   * read time would silently move labels — and orphan those mappings — the next time either side
   * was edited.
   */
  genericColumnsPosition: number | null;
  columns: EmpyreanSplitRuleColumn[];
}

/**
 * The newest Clear All Splits of one dataset that has not been put back: what its Restore Cleared
 * Splits button restores (PROD-2868). A Restore is a swap: the rules the dataset holds when it runs
 * are kept the same way first, so they can be put back in turn and nothing is lost either way.
 */
export interface EmpyreanRestorableSplitRules {
  /** the kept clear's id, which the Restore names so it puts back exactly the set the button offered */
  clearId: number;
  ruleCount: number;
  /** when the rules were removed, by the database's own clock */
  clearedAtUtc: Date;
}

/**
 * {@link EmpyreanSplitRule.genericColumnsPosition} resolved to the index the generic labels really
 * land at among a rule's own `ownColumnCount` columns: null is first, and an index past the end is
 * last — the stored value outlives the column list it indexes into.
 *
 * ⭐ **The stored scalar is not the placement.** `null` and `0` are the same place, and so are
 * `own.length` and anything beyond it, so two rules can store different numbers and produce
 * byte-identical codes. It lives here, beside the field, so that anything comparing two rules'
 * placement uses the resolver's own rule: one that compared the raw value would report a move every
 * time an analyst re-saved an untouched pre-PROD-486 rule (the modal writes the explicit `0` that null
 * always meant), and one that clamped differently from the resolver would miss a real one.
 */
export const resolveEmpyreanGenericColumnsPosition = (
  position: number | null | undefined,
  ownColumnCount: number
): number => Math.min(Math.max(position ?? 0, 0), ownColumnCount);

/**
 * A reusable, DataType-scoped split rule (not tied to a Portfolio Code or account code).
 * Users save the current column/group setup as a named template and apply it — copied into a
 * portfolio rule — when splitting any account code under the same DataType.
 */
export interface EmpyreanSplitRuleTemplate {
  id: number;
  name: string;
  processorType: string;
  dataType: string;
  columns: EmpyreanSplitRuleColumn[];
}

/**
 * A governed chart account a manual balance-sheet entry may post to: the non-'Total' (detail)
 * accounts, which carry their own parent, side and classification. These are NOT generated per
 * client — 'Cash and Due From' (1000400), 'Reserve for Loan Loss' (1041800) and the rest already
 * exist in the chart with fixed numbers, so a manual entry posts to the account the client's
 * Empyrean model already has rather than minting a duplicate child beneath the Total.
 */
export interface EmpyreanPostableAlmAccount {
  id: number;
  almAccountNum: string;
  accountName: string;
  /** the Total account this one rolls up to; drives the COA Summary grouping */
  parentAlmAccountNum: string | null;
  /** which chart this row belongs to; see {@link EmpyreanModelType} */
  modelType: EmpyreanModelType;
  /**
   * The governed side. **Read it; never re-derive it from the leading digit.** IRRM only ever says
   * Asset/Liability/Equity and its 1/2/3 rule is complete, but the CU ALM chart adds Income and
   * Expense and its digits do not decide between them — measured on dev, `9…` is 4 Income and 3
   * Expense. See `deriveAccountType` in `buildCoaTabRows.ts`.
   */
  accountType: string | null;
  /** position-bearing class (Overnight, Non-Maturity, Fixed Income, Posting, …) */
  classification: string | null;
  /** the governed mnemonic; emitted verbatim on the COA tab rather than deriving one over it */
  mnemonic: string | null;
  isActive: boolean;
  /**
   * The governed Rate Type (PROD-1723); null = not decided yet — see {@link EmpyreanChartRateType}.
   *
   * Optional, and absent reads exactly like null: the server always sends it, but every fixture and
   * caller written before PROD-1723 builds this type without it. Read it with `?? null`.
   */
  rateType?: EmpyreanChartRateType | null;
  /**
   * A Fixed Income account with no contractual maturity, which takes as-of + 30 years (PROD-465).
   * Null on every row until Luke confirms which accounts carry it: read null (or absent) as off.
   */
  noContractualMaturity?: boolean | null;
}

/**
 * How a governed chart account's rate behaves (PROD-1723, Luke 5 Oct 2026) — stored ON THE CHART,
 * per account, and read by the Balance Sheet Rate Type default (PROD-467), the 30-year maturity
 * auto-fill (PROD-465) and the fixed / variable loan default set (PROD-742).
 *
 * ⚠ **Not the Classification, and not derivable from it.** A Fixed Income account can hold
 * variable-rate positions, and Due from CDs is Fixed Income but matures. Null means "not decided":
 * the names decide most accounts, and FSG fills the rest (`emp-chart-rate-type-fsg-list.csv`).
 *
 * ⚠ A different vocabulary from {@link empyreanManualRateTypes} on purpose: these are Luke's words
 * for the account, those are the Balance Sheet grid's for a position (`Variable` ⇒ `Floating`).
 */
export const empyreanChartRateTypes = ['Fixed', 'Variable', 'Non-interest'] as const;
export type EmpyreanChartRateType = (typeof empyreanChartRateTypes)[number];

/** How a manual entry's rate behaves; drives which output fields are blanked. */
export const empyreanManualRateTypes = ['Fixed', 'Floating', 'Non-interest bearing'] as const;
export type EmpyreanManualRateType = (typeof empyreanManualRateTypes)[number];

/**
 * Where a balance entry came from (PROD-1466): keyed by hand, or pre-filled from a credit union's
 * NCUA 5300 call report. A re-import for the same client + As Of Date replaces that date's
 * `CallReport` rows and leaves its `Manual` rows alone; an edited imported row stays `CallReport`.
 */
export const empyreanManualBalanceSources = ['Manual', 'CallReport'] as const;
export type EmpyreanManualBalanceSource = (typeof empyreanManualBalanceSources)[number];

/**
 * One manually keyed balance-sheet line — the accounts that never arrive in a client core file
 * (cash, reserve for loan loss, fixed assets, accrued interest, capital, …) and have always been
 * entered by hand.
 *
 * Snapshotted per portfolio AND per As Of Date, so a generated file stays reproducible; the app's
 * copy-from-last-quarter action seeds a new quarter from the previous one instead of carrying
 * balances forward silently.
 */
export interface EmpyreanManualBalanceEntry {
  id: number;
  portfolioCode: string;
  /** yyyy-MM-dd; the workspace As Of Date this snapshot belongs to */
  asOfDate: string;
  /** governed detail account this entry posts to */
  almAccountNum: string;
  /** becomes InstrumentID on the output tab, so it reads as a label rather than a number */
  description: string;
  /** feeds both CurrFace and CurrBook; negative is legitimate (contra accounts) */
  balance: number;
  rateType: EmpyreanManualRateType;
  /** PERCENT as keyed (4.35 = 4.35%); the export divides by 100. Null when non-interest-bearing. */
  rate: number | null;
  /** yyyy-MM-dd; null for non-maturity items */
  maturityDate: string | null;
  /**
   * yyyy-MM-dd; a Floating line's next rate reset → NextResetDate (PROD-1547). Null on every other
   * Rate Type: a fixed or balance-only line has no reset to describe.
   */
  nextResetDate: string | null;
  /**
   * A Floating line's reset index → ResetIndex (PROD-1724), exactly as the model declares it: Empyrean's
   * mnemonics are case-sensitive. Null on every other Rate Type.
   */
  resetIndex: string | null;
  /** PERCENT as keyed (2.25 = 2.25%) like `rate`, over the index → ResetSpread (÷ 100 on export); Floating only */
  resetSpread: number | null;
  /** months between resets → ResetMths (PROD-1724); 0 = floats daily. Floating only */
  resetMths: number | null;
  /** yyyy-MM-dd; next call date of a callable structure → ExerciseDate */
  callDate: string | null;
  sortOrder: number | null;
  source: EmpyreanManualBalanceSource;
}

/** The two statements a 5300 call report fills: quarter-end balances, and year-to-date income lines. */
export const empyreanCallReportSheets = ['BalanceSheet', 'IncomeStatement'] as const;
export type EmpyreanCallReportSheet = (typeof empyreanCallReportSheets)[number];

/**
 * One line of a model type's NCUA 5300 call report map (PROD-1466, `emp-call-report-map.sql`): which
 * account code in the XML feeds which ALM account. CU ALM's is Greg's `XML Empyrean Mapping.xlsx`.
 * Lines with no code (Loan Interest Refunded, Fees on Loans) and the equation lines (Credit Losses,
 * Taxes) are deliberately absent: an import never touches them.
 */
export interface EmpyreanCallReportMapLine {
  sheet: EmpyreanCallReportSheet;
  almAccountNum: string;
  /** as the XML carries it: the 5300 code with an `A` prefix (`131` → `A131`) */
  accountCode: string;
  /** the other code of a two-code line (pre-CECL / CECL); the larger value wins, never the sum */
  altAccountCode: string | null;
  /** the account goes in with the opposite sign to the file's — the allowance, which ships negative */
  negate: boolean;
  /** Greg's row order */
  sortOrder: number;
}

/**
 * Which published starting-point sheet a portfolio's assumption defaults come from. Analyst-set:
 * there is no reliable bank/credit-union flag elsewhere in the estate today. The two sheets are
 * numerically identical and differ only in one category's name, so this drives terminology rather
 * than values — but it is the seam a real flag would slot into later.
 */
export const empyreanInstitutionKinds = ['Bank', 'CU'] as const;
export type EmpyreanInstitutionKind = (typeof empyreanInstitutionKinds)[number];

/**
 * Which governed chart of accounts a client's model is built from. Two charts share the table:
 * 641 `IRRM` rows and 276 `CU ALM` rows, keyed `(ModelType, ALMAccountNum)`.
 *
 * ⚠ **A SEPARATE AXIS FROM {@link EmpyreanInstitutionKind}, and not derivable from it.**
 * `institutionKind` (Bank/CU) picks which published assumptions sheet a portfolio inherits;
 * `modelType` picks which chart it uses. Today SELF is the only `CU` client and the only
 * `CU ALM` one, so `CASE WHEN institutionKind = 'CU' THEN 'CU ALM' ELSE 'IRRM' END` would give
 * the right answer for all 23 — and would be wrong, silently and inside a delivered BRD, for
 * the first IRRM credit union that arrives. Mike, 2026-09-03: *"not EVERY credit union is CU ALM
 * model type, but most will be."* Two axes, two values, each stated rather than inferred.
 *
 * ⚠⚠ **`'CU ALM'` is ALSO a processor type** — a core-platform name in
 * `emp.EmpyreanProcessorTypes`, matched against `EmpyreanFileSettings.ProcessorType` and
 * `EmpyreanAccountCodeMappings.ProcessorType`. Same string, unrelated meaning. Never rename
 * either to disambiguate, and never widen a signature to accept "a processor type or a model
 * type": a parameter takes one or the other, and the name says which.
 */
export const empyreanModelTypes = ['CU ALM', 'IRRM'] as const;
export type EmpyreanModelType = (typeof empyreanModelTypes)[number];

/**
 * What a chart read uses when a client has not been asked yet (`modelType` null).
 *
 * ⚠ **This is a fallback, not a default, and it exists in exactly one place on purpose.** Every
 * one of the 23 configured clients on dev carries an explicit `ModelType`, so nothing reaches
 * this today; it is here so that a client registered *after* this change — by a file setting or a
 * mapping save, neither of which asks — keeps reading the chart it read before the column
 * existed, rather than getting an empty chart or a coin flip between two charts that share 94
 * account numbers. Card #405 replaces it with the load-time prompt, which is the only thing that
 * can turn "nobody has chosen" into an answer. Do not spread `?? 'IRRM'` past
 * `resolvePortfolioModelType`.
 */
export const EMPYREAN_FALLBACK_MODEL_TYPE: EmpyreanModelType = 'IRRM';

/** One chart-keyed store, and how much of it a model-type switch would strand. */
export interface EmpyreanModelTypeImpactItem {
  /** already pluralised for display — see {@link EmpyreanClientRemoval}'s items, same reason */
  label: string;
  /** rows the client owns in this store */
  total: number;
  /** of those, the ones whose account number does not exist in the TARGET chart */
  orphaned: number;
}

/**
 * What changing a client's chart would cost, itemised — fetched before the confirmation opens so
 * the dialog can name the damage instead of describing it.
 *
 * ⚠ **In practice this is all-or-nothing, and that is a measured fact rather than a guess.** The
 * two charts share 94 account numbers, which reads as "some mappings would survive" — but on dev
 * (2026-09-07) **not one client** has a mapping parent that exists in the CU ALM chart: the shared
 * numbers are not the ones mappings point at. So the honest count is per-store `orphaned`, computed
 * against the target chart, not the overlap arithmetic.
 *
 * Five stores, because these are the five that key on a chart account:
 * `EmpyreanAccountCodeMappings.ParentALMAccountNum`, and `ALMAccountNum` on
 * `EmpyreanPortfolioAccountStates`, `EmpyreanPortfolioAssumptions`,
 * `EmpyreanManualBalanceEntries` and `EmpyreanIncomeStatementTargets` (PROD-1515). File settings
 * and split rules describe a FILE's layout and carry no account number, so a switch cannot touch
 * them — leaving them out of the warning is deliberate.
 */
export interface EmpyreanModelTypeSwitchImpact {
  portfolioCode: string;
  /** null when nobody has chosen yet — the load prompt's case, where nothing can be stranded */
  currentModelType: EmpyreanModelType | null;
  targetModelType: EmpyreanModelType;
  /** every chart-keyed store, including the ones at zero, so the dialog can show a complete tally */
  items: EmpyreanModelTypeImpactItem[];
  /** the sum across items; zero means the switch is free and needs no typed confirmation */
  orphanedTotal: number;
}

/**
 * The behavioral assumptions Empyrean models a balance sheet with. Each becomes rows in one of the
 * BRD's EMP_Lookups tables: prepay speeds and decay feed the prepayment tables, beta and lag the
 * pricing tables, newVolume the rate new business is booked at, and floor and cap (PROD-2106, CU ALM
 * only) their own floors and caps tables.
 */
export const empyreanAssumptionFields = ['prepay', 'beta', 'lag', 'decay', 'newVolume', 'floor', 'cap'] as const;
export type EmpyreanAssumptionField = (typeof empyreanAssumptionFields)[number];

/**
 * One vector of the shop's ASSUMPTIONS DEFAULT — governed, shop-wide, one row per
 * (institution kind, category, field).
 *
 * Sourced from the published starting-point sheet (`IRRM - Initial Assumptions - New CD Betas
 * 2026.xlsx`). That sheet covers prepay, beta and lag; decay, new-volume rates and the overnight
 * categories are house placeholders until real numbers land, which is what `isPlaceholder` marks so
 * the tab can badge them rather than passing them off as published figures.
 */
export interface EmpyreanAssumptionDefault {
  id: number;
  institutionKind: EmpyreanInstitutionKind;
  /** assumption category, e.g. CONSUMER, NOW_SAV, JUMBO_TIME */
  setCode: string;
  /** display name; the one thing that differs between the bank and credit-union sheets */
  setName: string;
  field: EmpyreanAssumptionField;
  /** seven comma-separated ladder-aligned values, or a single value for newVolume */
  vector: string;
  /** a house placeholder rather than a published number */
  isPlaceholder: boolean;
  /** balance-sheet flow, so category lists read cash → loans → deposits → equity */
  sortOrder: number;
  isActive: boolean;
}

/**
 * One vector of a CLIENT-SPECIFIC ASSUMPTIONS PROFILE. A null `vector` means the row inherits its
 * default, so a profile stores only what this bank differs on and a later change to the default
 * still reaches everyone who never overrode it.
 *
 * Not keyed by As Of Date, unlike the manual balance entries: a balance is a quarterly fact, an
 * assumption is a setting that stands until someone changes it. `almAccountNum` is normally a
 * 'Total' parent — the level an analyst states an assumption at — and the export fans each vector
 * out to one row per posting account beneath it.
 */
export interface EmpyreanPortfolioAssumption {
  id: number;
  portfolioCode: string;
  /** the chart account this assumption is stated at */
  almAccountNum: string;
  field: EmpyreanAssumptionField;
  /** null = inherit the default for `setCode` */
  vector: string | null;
  /** the default category this row inherits from, and what a reset restores to */
  setCode: string;
  /** last edit; the staleness signal, since these carry across quarters untouched */
  modifiedDate: Date | null;
}

/**
 * How a partition field is entered and written: `list` is a dropdown over its options, `number` a
 * numeric input written as a numeric cell, `text` free entry written as text.
 */
export const empyreanPartitionValueKinds = ['list', 'number', 'text'] as const;
export type EmpyreanPartitionValueKind = (typeof empyreanPartitionValueKinds)[number];

/**
 * One column of a Partitions tab — Empyrean's partition parameter, by the header it imports under.
 *
 * ⚠⚠ **Identified by `(classification, fieldName)`, never by `fieldName` alone.** Fixed Income and
 * Non-Maturity share 24 header names but not always their lists (`Apply Sprd Adj` differs), because
 * each Empyrean calculator class is its own property set.
 */
export interface EmpyreanPartitionField {
  id: number;
  /** the chart's Classification string — `Fixed Income`, `Non-Maturity`, ... */
  classification: string;
  /** the column header, VERBATIM: Empyrean matches it to the partition editor's parameter name */
  fieldName: string;
  /** the group heading the editor shows it under (Accounting, Init Pricing, ...) */
  section: string | null;
  valueKind: EmpyreanPartitionValueKind;
  /** column order on the tab */
  sortOrder: number;
  isActive: boolean;
}

/** One allowed value of a `list` field — a row of Greg's `Dropdowns` tab. */
export interface EmpyreanPartitionFieldOption {
  id: number;
  classification: string;
  fieldName: string;
  optionValue: string;
  sortOrder: number;
  isActive: boolean;
}

/**
 * A governed parent default: every child of `almAccountNum` (a parent) inherits `value` for this
 * field unless the client overrides it. A field with no row is an EMPTY cell — Empyrean reads that
 * as "leave the parameter alone", which is not the same as 0.
 */
export interface EmpyreanPartitionDefault {
  id: number;
  modelType: EmpyreanModelType;
  almAccountNum: string;
  classification: string;
  fieldName: string;
  /** always text; the field's `valueKind` says how to write it */
  value: string;
}

/**
 * One client's override of one field on one generated child, keyed on the child's PERSISTED
 * account number. An unsaved child has no stable number, so it can only take its parent's defaults.
 */
export interface EmpyreanPortfolioPartitionOverride {
  id: number;
  portfolioCode: string;
  almAccountNum: string;
  classification: string;
  fieldName: string;
  value: string;
  modifiedDate: Date | null;
  modifiedBy: string | null;
}

/** Portfolio-scoped settings. Its own record because file settings are keyed per Data Type. */
export interface EmpyreanPortfolioSettings {
  id: number;
  portfolioCode: string;
  institutionKind: EmpyreanInstitutionKind;
  /**
   * Which governed chart this client's model is built from.
   *
   * **Null means nobody has chosen** — not "IRRM". The column is nullable for the same reason
   * `institutionKind` is analyst-set: a NOT NULL column needs a default, and a default is a guess
   * presented as a decision. Chart reads route the null through
   * {@link EMPYREAN_FALLBACK_MODEL_TYPE} so a client registered without being asked keeps the
   * chart it had; Card #405 adds the prompt that removes the guess.
   */
  modelType: EmpyreanModelType | null;
  /** the client's core; null while a client is registered but its processor type isn't known yet */
  processorType?: string | null;
  /**
   * The client's own tax rate and CPI (PROD-647), DECIMAL rates (0.29 = 29%). **Null means inherit**
   * the model type's default (`EmpyreanModelTypeSettings`) — resolved by `resolveClientProfile`.
   */
  taxRate?: number | null;
  cpi?: number | null;
}

/**
 * A client the Empyrean tool already knows, as offered by the staging screen's client picker.
 *
 * Descriptive, like {@link EmpyreanDataTypeVariant}: it lists the clients that are configured, not the
 * clients that are permitted. A code that isn't here can still be typed — that is how a new client
 * gets set up — but it is shown as new rather than resolving silently against a processor default,
 * which is what made `NORM` for `NORF` cost a debugging round.
 */
/**
 * One governed chart account seen through one client's eyes.
 *
 * Only governed accounts appear — the Total parents and the detail accounts manual balance entries
 * post to. Generated children are not in the chart at all, so a child is active exactly when its
 * account-code mapping exists; removing the mapping is the single way to stop emitting a code.
 */
export interface EmpyreanPortfolioChartAccount {
  almAccountNum: string;
  accountName: string;
  /**
   * The chart this client's rows came from, resolved server-side from its
   * {@link EmpyreanPortfolioSettings.modelType}. Returned rather than assumed so the grid can
   * never render one chart while labelling it the other.
   */
  modelType: EmpyreanModelType;
  parentAlmAccountNum: string | null;
  /** the governed side, straight from the chart — see {@link EmpyreanPostableAlmAccount.accountType} */
  accountType: string | null;
  classification: string | null;
  mnemonic: string | null;
  displayOrder: number | null;
  /** the governed Rate Type — see {@link EmpyreanPostableAlmAccount.rateType} (optional the same way); null on a Total */
  rateType?: EmpyreanChartRateType | null;
  /** see {@link EmpyreanPostableAlmAccount.noContractualMaturity}; null or absent reads as off */
  noContractualMaturity?: boolean | null;
  /**
   * The governed `Description`, which is **NULL on all 917 rows of both charts** — measured on dev
   * 2026-09-08. The column exists so a client can override it; nothing governed populates it yet.
   * Read {@link EmpyreanPortfolioChartAccount.descriptionOverride} alongside this, never instead.
   */
  description: string | null;
  /**
   * `Auto`, `1st`, `16th` or a `…/Rollover` variant — see `TRADING_DAY_OPTIONS`. Every CU ALM
   * balance-sheet row carries `Auto/Rollover` and every income-statement row carries NULL; IRRM is
   * uniformly `1st`. ⚠ Not the same thing as the `ALM COA` worksheet's hardcoded `'1st'`, which is
   * a separate artifact — see `COA_TAB_HEADERS`.
   */
  tradingDay: string | null;
  /** 0 on all 641 IRRM rows; CU ALM has 272 zeros, 2 nulls, one 2000 and one 10000 */
  calculateGroup: number | null;
  /**
   * The 8 `Post *` columns, in worksheet order.
   *
   * ⚠ These are **ALM account NUMBERS, not flags** — `PostAccruals = 40200000` means "post accruals
   * to Tax Exempt Interest Income". They read as strings because the column is `bigint`, the same
   * reason {@link almAccountNum} does.
   *
   * Populated on **15 CU ALM rows and 0 IRRM rows**, which is why the `ALM COA` worksheet can leave
   * its own posting columns blank without losing anything.
   */
  postAccruedNP: string | null;
  postAccruals: string | null;
  postDiscPrem: string | null;
  postGainLoss: string | null;
  postFVGL: string | null;
  postFVOperating: string | null;
  postIntangible: string | null;
  postNonAccrual: string | null;
  /**
   * This client's override of {@link description}, or null for "no override".
   *
   * ⚠ Null is **not** the same as an empty string: an analyst may deliberately override a
   * description to blank. Resolve the pair with `resolveChartOverrides`, never with `||`.
   */
  descriptionOverride: string | null;
  /** this client's override of {@link displayOrder}, or null for "no override". 0 is a real value. */
  displayOrderOverride: number | null;
  /** an aggregation ('Total') node; mappings roll up to these */
  isParent: boolean;
  /** a root total — never togglable, since deactivating it would empty the chart beneath it */
  isGrandTotal: boolean;
  /** the governed shop-wide flag; false ⇒ never active for anyone */
  chartIsActive: boolean;
  /** something populates this account for this client right now */
  litNow: boolean;
  /** something populated it at least once before — the sticky half */
  everLit: boolean;
  /** the analyst's override; null = none */
  manualActive: boolean | null;
  /** `(manualActive ?? (everLit OR litNow)) AND chartIsActive` — what the export honours */
  effectiveActive: boolean;
  /** plain-English why, for the row tooltip */
  reason: string;
  mappingCount: number;
  manualEntryCount: number;
  incomeStatementTargetCount: number;
}

/**
 * One generated child account a client already has SAVED, from any Data Type.
 *
 * ⚠ Distinct from {@link EmpyreanAccountCodeMapping}, which is a dataset's mapping row. This is the
 * chart's view: the accounts the client's Empyrean model contains, whether or not the Data Type that
 * minted them happens to be loaded.
 */
export interface EmpyreanPortfolioChildAccount {
  almAccountNum: string;
  parentAlmAccountNum: string;
  /** the analyst's description, falling back to the raw account code */
  name: string;
  /** breaks a tie in the child's Classification, which its PARENT decides — see `resolveChildClassification` */
  dataType: string;
  /** the analyst's Fixed / Variable choice on the mapping that mints it (PROD-1727); absent / null = none */
  rateType?: EmpyreanLoanRateType | null;
}

export interface EmpyreanClient {
  portfolioCode: string;
  /**
   * Which chart the client's model uses, or null when nobody has been asked. Carried on the client
   * row so the picker and the load flow can see it without a second round trip — the load prompt
   * (#405) keys on exactly this null.
   *
   * ⚠ Unrelated to {@link processorType}, even though `CU ALM` is a value of both.
   */
  modelType: EmpyreanModelType | null;
  /**
   * Bank or credit union, from the client row — null when the client has no registry row yet. Picks
   * the assumptions defaults sheet and, on IRRM, which parents the Account Codes picker offers.
   *
   * ⚠ Read it with {@link institutionKindConfirmed}. Most rows were SEEDED with a guess from the
   * processor type (1 of 24 prod clients confirmed, 1 Oct 2026), so the value alone is not a decision.
   */
  institutionKind: EmpyreanInstitutionKind | null;
  /**
   * Whether an analyst has confirmed {@link institutionKind} — the row's `ModifiedDate` is set. The
   * Load prompt (PROD-1546) asks while this is false, the way #405's asks while `modelType` is null,
   * and the picker scopes by Bank/CU only once it is true.
   */
  institutionKindConfirmed: boolean;
  /** null only while a client is registered but nothing has been configured for it yet */
  processorType: string | null;
  /**
   * The bank's real name, once the master Portfolios list backs this picker. Always null today —
   * that list lives on another database connection behind its own feature, and searching the whole
   * pool of portfolios by name is a separate feature from "which clients does Empyrean know".
   */
  displayName: string | null;
  /** how many (Data Type, Variant) datasets this client is configured for */
  dataTypeCount: number;
  /** the most recent As Of Date generated for this client, or null when it has never been generated */
  lastAsOfDate: string | null;
  /**
   * Every As Of Date this client already has generated data for. Generating replaces a snapshot by
   * (PortfolioCode, DataType, AsOfDate), so this is what lets the screen warn *before* an overwrite
   * rather than after it.
   */
  generatedAsOfDates: string[];
}

/**
 * Both assumption ladders — the ±300bp rate shocks and the decay months-forward buckets — are seven
 * columns wide, which is what lets one stored vector shape serve every field. Empyrean's own column
 * headers, so this is a contract rather than a setting. Shared by the app's vector parsing and the
 * API's save validation so the two cannot drift.
 */
export const EMPYREAN_ASSUMPTION_VECTOR_LENGTH = 7;

/** Assumption fields stored as a single value rather than a ladder-aligned vector (the 1-D tables). */
export const empyreanAssumptionScalarFields: readonly EmpyreanAssumptionField[] = ['newVolume', 'floor', 'cap'];

// ── the client workspace: who an analyst's clients are ──────────────────────────────────────────

/**
 * Did the Generate itself finish. Separate from {@link EmpyreanRunArchiveStatus} because the two fail
 * independently — a workbook can be built and its inputs fail to archive, or every byte can be stored
 * by a Generate that then throws.
 */
export type EmpyreanRunStatus = 'PENDING' | 'COMPLETE' | 'FAILED';

/**
 * Are the run's input bytes there.
 *
 * `UNAVAILABLE` is **not** a failure: it means this environment has nowhere to put them, which is the
 * state of every environment until phase C ships. Keeping it apart from `PENDING` ("on its way") and
 * `PARTIAL` ("we tried and did not finish") is the whole point — collapsing "correctly gone" into
 * "broken" is the silent-failure class this feature is built to avoid, and retention guarantees the
 * question comes up: run rows outlive their bytes by design.
 */
export type EmpyreanRunArchiveStatus = 'UNAVAILABLE' | 'PENDING' | 'COMPLETE' | 'PARTIAL' | 'EXPIRED';

/** One client input file that fed a run. */
export interface EmpyreanRunFile {
  runFileId: number;
  /** staging-list position, so a replay rebuilds the list in the order the analyst built it */
  stagingOrder: number;
  dataType: string;
  /** null = the Data Type's default (single) record layout */
  variant: string | null;
  originalFileName: string;
  sizeBytes: number;
  /**
   * null = **not computed**, not "no hash". Computed in the browser at Generate, which needs a secure
   * context; phase C2's upload commit must read null as "cannot verify" rather than as a pass.
   */
  contentSha256: string | null;
  /** null until phase C's upload commits — the presence of bytes, per file */
  blobKey: string | null;
}

/**
 * One Generate press, and the files that fed it.
 *
 * Identified by `runId`, never by `(portfolioCode, asOfDate)`: a corrected re-run of the same as-of
 * date is a second run, and a date-only key would let it overwrite the first.
 */
export interface EmpyreanRun {
  runId: string;
  portfolioCode: string;
  processorType: string;
  asOfDate: string;
  /** ISO instant */
  generatedAtUtc: string;
  /** the Entra sign-in address of whoever pressed Generate */
  generatedBy: string;
  workbookFileName: string;
  /** the worksheets actually written — not every Data Type the client has configured */
  includedDataTypes: string[];
  status: EmpyreanRunStatus;
  archiveStatus: EmpyreanRunArchiveStatus;
  /** ISO instant, or null when no retention window applies */
  expiresAtUtc: string | null;
  /**
   * The `GenerateOptions` the analyst chose, verbatim JSON — which worksheets, and whether the COA,
   * assumption and manual-balance tabs were written.
   *
   * ⚠ **Written by whatever version of the app pressed Generate**, so a reader must parse it
   * defensively: an unparseable or unfamiliar value means *do not restore*, never a crash and never a
   * half-restored dialog. Stored since phase B1 and exposed here in C3, which is the first phase with
   * something to do with it.
   */
  generateOptions: string | null;
  files: EmpyreanRunFile[];
  /**
   * `WORKBOOK` for every run before PROD-2217; `IRRM2` for an engine run, which has no input files and
   * no Reload; `SAVED` for a Save Run (PROD-2742), which kept its files without generating and has no
   * workbook name. Optional because a client may read an API that predates it: absent means `WORKBOOK`.
   */
  runKind?: EmpyreanRunKind;
  /** the engine side of an `IRRM2` run; null for a workbook run */
  engineRun?: EmpyreanEngineRun | null;
}

/** One staged file, as the client describes it to {@link EmpyreanRun} at Generate. */
export interface EmpyreanRunFileInput {
  stagingOrder: number;
  dataType: string;
  variant: string | null;
  originalFileName: string;
  sizeBytes: number;
  contentSha256: string | null;
}

/** What the app hands over when a Generate, or a Save Run, begins. */
export interface EmpyreanRunInput {
  portfolioCode: string;
  processorType: string;
  asOfDate: string;
  /**
   * `SAVED` for Save Run (PROD-2742); absent or `WORKBOOK` for a Generate. Never `IRRM2`: an engine run is
   * opened by `beginEmpyreanEngineRun`, which records its payload, not its files.
   */
  runKind?: 'WORKBOOK' | 'SAVED';
  /** the Empyrean file a Generate writes; `''` for a saved run, which writes none (the column is NOT NULL) */
  workbookFileName: string;
  includedDataTypes: string[];
  /** the `GenerateOptions` object verbatim, as JSON */
  generateOptions: string | null;
  files: EmpyreanRunFileInput[];
}

/**
 * What {@link EmpyreanRunInput} produced: the run, and an id per file to address its chunks with.
 *
 * The ids are why this is not just a RunId string — a chunked upload has to name the file it is a
 * chunk of, and `stagingOrder` is how the client pairs each id back to the row it staged.
 */
export interface EmpyreanRunHandle {
  runId: string;
  files: { runFileId: number; stagingOrder: number }[];
}

/**
 * Whether this environment can archive a run's input files, and on what terms.
 *
 * ⚠ **`enabled: false` is an ordinary answer, not an error.** An environment with no storage account
 * configured keeps its run history and simply never stores bytes — the runs read `UNAVAILABLE` and
 * replay asks for the files. `reason` says which piece of configuration is absent, so an operator is
 * not left guessing; it is never a credential.
 */
export interface EmpyreanRunArchiveConfig {
  enabled: boolean;
  /** raw bytes per chunk the client should send; base64 inflates it ~33% in transit */
  chunkSizeBytes: number;
  /** account and container, safe to log — null when not configured */
  location: string | null;
  reason: string | null;
}

/** One archived file, once its chunks are committed and checked. */
export interface EmpyreanRunFileArchiveResult {
  runFileId: number;
  blobKey: string;
  sizeBytes: number;
  /**
   * Whether the stored bytes were checked against the SHA-256 the run recorded.
   *
   * `false` means the run carried no hash to check against — the size matched and nothing more can be
   * said. It must never read as "verified": the same three-way honesty the replay panel keeps.
   */
  hashVerified: boolean;
}

/** Where a run's archive ended up, once every file has been attempted. */
export interface EmpyreanRunArchiveOutcome {
  archiveStatus: EmpyreanRunArchiveStatus;
  fileCount: number;
  uploadedCount: number;
  /** the files that did NOT archive, named so a warning can say which — not merely how many */
  missingFileNames: string[];
}

/**
 * What replay can fetch back for one recorded run — phase C2b, the read half of the archive.
 *
 * ⚠ **`available: false` is an ordinary answer.** Four of the five archive statuses mean the bytes
 * are not there to fetch, and each for a different reason an analyst can act on differently — never
 * archived, still uploading, upload failed, deleted by retention. `reason` carries that sentence and
 * replay falls through to phase B2's re-attach, which is unchanged and still the only path for every
 * run recorded before the archive existed.
 */
export interface EmpyreanArchivedRunFiles {
  runId: string;
  /** the run's archive status AFTER the retention clock is applied, not the raw column */
  archiveStatus: EmpyreanRunArchiveStatus;
  available: boolean;
  reason: string | null;
  /** raw bytes per chunk to ask for; the base64 response inflates it ~33% */
  chunkSizeBytes: number;
  files: EmpyreanArchivedRunFile[];
}

/**
 * One fetchable file.
 *
 * ⭐ Everything here is read from the **`BlobKey` stored on the row**, never from rebuilding the key.
 * The stored key is what the bytes were actually written under; a rebuilt one would silently diverge
 * the day sanitisation, the env segment or the ordering changed, and point confidently at nothing.
 *
 * `sizeBytes` is what the STORE reports, not what the row recorded. They were checked equal at
 * commit, so a disagreement now means the object changed underneath us — and the number the chunk
 * arithmetic uses has to be the one the bytes actually have.
 */
export interface EmpyreanArchivedRunFile {
  runFileId: number;
  stagingOrder: number;
  originalFileName: string;
  sizeBytes: number;
  /** how many chunk requests this file takes at `chunkSizeBytes`; a zero-byte file still takes one */
  chunkCount: number;
  /** null = the run recorded no hash, so a fetched file can be size-checked and nothing more */
  contentSha256: string | null;
}

/**
 * What reconciling the archive found, in both directions.
 *
 * Per plan §3.5, an archive drifts two ways and only one of them is visible from the database: rows
 * whose blob is missing, and blobs no row claims. Reported rather than thrown — the caller is an
 * operator asking "what is wrong here", and a GraphQL error answers less.
 *
 * ⚠ **Nothing is written unless `apply` was asked for, and nothing is deleted unless `deleteOrphans`
 * was asked for as well.** The default is a pure report.
 */
export interface EmpyreanRunArchiveReconcileReport {
  /** account and container, safe to log */
  location: string;
  envSegment: string;
  /** the key prefix actually scanned, so a report says what it covered rather than implying everything */
  prefix: string;
  applied: boolean;
  deletedOrphansRequested: boolean;
  rowsChecked: number;
  blobsListed: number;
  /** ⚠ true = there are MORE blobs than were listed, so "no orphans" would be a false statement */
  listTruncated: boolean;
  /** rows inside the retention window whose blob is gone — a failure, not expiry */
  missingBlobs: EmpyreanRunArchiveMissingBlob[];
  /** rows whose blob is gone because retention removed it — correct, and counted separately */
  expiredRowCount: number;
  runsMarkedPartial: string[];
  orphanBlobs: EmpyreanRunArchiveOrphanBlob[];
  orphansDeleted: number;
  /** orphans left alone for being younger than the age floor — an in-flight Generate is not an orphan */
  orphansSkippedTooNew: number;
  /**
   * aged orphans left alone because the blob changed between the listing and the delete — the store
   * refused the etag-pinned delete (PROD #694). Not an error: the object there now is not the one the
   * listing judged, and the next reconcile judges it afresh.
   */
  orphansSkippedChanged: number;
  /** anything that failed mid-scan, named; a partial reconcile must not read as a clean one */
  errors: string[];
}

export interface EmpyreanRunArchiveMissingBlob {
  runId: string;
  portfolioCode: string;
  runFileId: number;
  originalFileName: string;
  /** the key the row claims — reported verbatim, because it is the evidence */
  blobKey: string;
}

export interface EmpyreanRunArchiveOrphanBlob {
  key: string;
  bytes: number;
  modifiedMs: number;
  deleted: boolean;
}

/**
 * The file's column layout, in file order — the same derivation the parser uses.
 *
 * A saved setting keeps two kinds of row in one list: **layout** rows (no Empyrean field, one per
 * file column, `sortOrder` = file position) and **field-mapping** rows (an Empyrean field fed by a
 * source column). Both carry a `columnName`, so ordering by `sortOrder` and keeping the first
 * occurrence of each name lets the layout rows fix the file order — mapping rows are always written
 * after them.
 *
 * ⭐ One rule shared by the parser (`getFileColumns`) and the layout divergence check
 * (`diffFileSettings`), which has to answer "did the file column order change" with the SAME rule the
 * parse applies. Two copies of this would eventually disagree, and the check would then be confidently
 * wrong about the one thing it exists to report.
 */
export const empyreanFileColumnOrder = (
  columns: Pick<EmpyreanFileSettingColumn, 'columnName' | 'sortOrder'>[] | null | undefined
): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  [...(columns ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach((column) => {
      if (column.columnName && !seen.has(column.columnName)) {
        seen.add(column.columnName);
        ordered.push(column.columnName);
      }
    });
  return ordered;
};

/**
 * An analyst who reaches a client, resolved through `emp.EmpyreanAnalystBrokers` → the client's
 * primary broker.
 *
 * The **email is the identity** and the name is decoration: assignments are keyed on the Entra
 * sign-in address, and `BackOff.Broker` is only consulted to make that address human-readable. A
 * null `name` therefore means "we could not put a face to this address", not "no analyst" — the
 * grid shows the address itself in that case rather than a blank cell.
 */
export interface EmpyreanClientAnalyst {
  email: string;
  /** `BackOff.Broker.FULLNAME`, or null when the address resolves to no broker or an ambiguous one */
  name: string | null;
}

/**
 * One row of the client grid at the top of the Empyrean screen — a client the firm has, annotated
 * with whatever Empyrean knows about it.
 *
 * **The union, not the intersection.** {@link EmpyreanClient} is only the clients Empyrean is
 * *configured* for (23 on dev); this directory is every client in the firm's book, so a bank that
 * still needs setting up can be found and picked. Empyrean's own facts (`processorType`,
 * `dataTypeCount`, `lastAsOfDate`) are therefore all nullable/zero here, and `isConfigured` says
 * which side a row came from. A configured client that is somehow absent from the customer book is
 * still emitted — with `institutionName` null — because dropping it would hide from the grid a
 * client the picker below still offers.
 */
export interface EmpyreanClientDirectoryEntry {
  portfolioCode: string;
  /** null when the code is configured in Empyrean but absent from the customer book */
  institutionName: string | null;
  institutionCity: string | null;
  institutionState: string | null;
  /**
   * `PortfolioCustomerLevelDetails.primaryBroker` — the 3-char INIT, and the only assignment hop
   * that exists in the database. Null for a row with no customer-book record.
   */
  brokerInit: string | null;
  /**
   * `BackOff.Broker.FULLNAME` for that INIT. **Null is a real case, not a bug** — one INIT in the
   * customer book (`XXX`) resolves to no broker row at all on dev, so the grid shows the INIT
   * chip alone rather than an empty Broker cell.
   */
  brokerName: string | null;
  /**
   * The customer book also splits a client between brokers (`isSplit` / `splitBrokerCode`), so a
   * client can legitimately belong to two analysts. `My Clients` matches `brokerInit` only; these
   * two are carried so the grid can *show* a split without yet claiming to resolve one.
   */
  isSplit: boolean;
  splitBrokerCode: string | null;
  /**
   * The analysts this client reaches, via `emp.EmpyreanAnalystBrokers` on {@link brokerInit}.
   *
   * **Matched on the PRIMARY broker only**, exactly as `filterClientsByScope` does, so the column and
   * the My/All toggle can never tell different stories about who a client belongs to. A split client
   * may in truth reach a second analyst; that is what the `split` badge says, and resolving it needs
   * broker-portal's `PortfolioAssignmentService` (two datasources `apps/api` lacks).
   *
   * Empty is the ordinary state, not an error — no analyst has assignments until an admin keys them.
   * The grid only shows this column under `All Clients`: under `My Clients` the answer is the person
   * reading it, so a column repeating their own name in every row is pure noise.
   */
  analysts: EmpyreanClientAnalyst[];
  /** null until a bank is configured — which is what `isConfigured` false means */
  processorType: string | null;
  /**
   * Which chart the client's model uses. Null covers both "not configured at all" and "configured
   * but never asked", which is why the grid shows it as unset rather than as IRRM.
   */
  modelType: EmpyreanModelType | null;
  isConfigured: boolean;
  /** how many (Data Type, Variant) datasets Empyrean holds for this client; 0 when unconfigured */
  dataTypeCount: number;
  /** the most recent As Of Date Empyrean has generated, or null when it never has */
  lastAsOfDate: string | null;
  /** the latest as-of the customer book holds data for — how current the client is, from outside Empyrean */
  bbaMostRecentAsOfDate: string | null;
  /**
   * **null = the intake pipeline is not wired, i.e. we do not know**; 0 = wired and nothing new;
   * n = n new files waiting.
   *
   * Always null today: client files land on-prem (`MBPFileUploads` → BBF → `MyPortfolioUploads`,
   * CU files via ShareFile) and none of that reaches this repo. The column is designed now and
   * disabled in the UI so that when intake lands only the resolver changes. It must never be
   * defaulted to 0 — "nothing new" and "no idea" are different answers and the whole point of the
   * column is the first one.
   */
  newFileCount: number | null;
}

/**
 * The client grid's whole payload: every client, plus the two facts that decide what the screen may
 * show the caller. One query rather than three, because `My Clients` is then a local filter over
 * rows already in hand — the toggle is instant and cannot disagree with the grid beside it.
 */
export interface EmpyreanClientDirectory {
  clients: EmpyreanClientDirectoryEntry[];
  /**
   * The caller's own sign-in address, as the server sees it. Returned rather than read client-side
   * because this screen runs in two hosts and only one of them has an identity provider mounted — and
   * because the admin editor keys grants on exactly this string, so the value shown must be the value
   * the server would match.
   */
  callerEmail: string | null;
  /**
   * The broker INITs assigned to the CALLER. Empty means nobody has assigned them any — which is
   * the state every analyst is in until the assignments are keyed, and why `My Clients` must render
   * disabled-with-a-reason rather than as an empty grid.
   */
  myBrokerInits: string[];
  /**
   * The caller is an Emport admin (a row in `emp.EmpyreanAdmins`, PROD-744 — no longer the Entra
   * `BakerAdmin` role), so every admin screen opens: the analyst→broker assignments, the governed
   * partition defaults. Server-sourced on purpose: the front end must not compute admin from a host-specific hook, and the flag the
   * button reads is then the same one the mutation guard enforces.
   */
  isEmportAdmin: boolean;
  /**
   * The broker-name lookup (`BackOff.Broker`, a different database from the customer book) failed,
   * so every `brokerName` is null for a reason that is NOT "this broker has no name". Third state,
   * kept distinct so the Broker column can say "names unavailable" instead of silently reading as
   * a book full of nameless INITs.
   */
  brokerNamesUnavailable: boolean;
}

/**
 * An analyst→broker grant. The missing hop: the customer book gives broker→clients, and this gives
 * analyst→broker, so "Bob's clients" = every client whose `primaryBroker` is one of Bob's INITs.
 *
 * Following Ledger's authority pattern — Keystone (or baker) grants *access* to the app, the app
 * owns *authority* in its own table with its own admin editor. Revoking is DELETING the row; there
 * is deliberately no third "none" state to pick out of the same control.
 */
export interface EmpyreanAnalystBrokerAssignment {
  id: number;
  /** matches the caller's Entra email; the authority, and compared case-insensitively */
  analystEmail: string;
  brokerInit: string;
  /** `BackOff.Broker.FULLNAME`, or null when the INIT resolves to no broker row */
  brokerName: string | null;
  /** how many clients this one grant actually reaches, so an admin sees the size of what they gave */
  clientCount: number;
  assignedBy: string;
  assignedAtUtc: string;
}

/** A broker the admin editor can grant, from `BackOff.Broker`. */
export interface EmpyreanBrokerOption {
  init: string;
  name: string | null;
  /** how many clients in the customer book name this broker as primary — 0 is worth seeing before granting */
  clientCount: number;
}

/**
 * What deleting a run removed — PROD-505.
 *
 * A run owns its history row, its settings snapshot and its archived bytes. It does NOT own
 * `emp.EmpyreanGeneratedData`, which is keyed `(ProcessorType, PortfolioCode, DataType, AsOfDate)`
 * with no RunId on it — so a delete leaves the generated rows alone (Mike, 2026-09-17), and there is
 * nothing about them for this result to report.
 */
export interface EmpyreanRunDeleteResult {
  runId: string;
  portfolioCode: string;
  asOfDate: string;
  fileRowsDeleted: number;
  blobsDeleted: number;
  /** blobs whose bytes could not be removed; the reconciler sweeps them later */
  blobsFailed: number;
}

/**
 * The most rows one generated-data chunk may carry — PROD-639. The client's chunker splits at it and
 * the API's `EmpyreanGeneratedDataChunkSaveDto.rows` refuses more, so both read this one value rather
 * than two literals that could drift apart (PR #1965 review).
 */
export const EMPYREAN_GENERATED_DATA_CHUNK_MAX_ROWS = 50_000;

/**
 * One standard row of the Empyrean `Rate Index` tab — PROD-649. A governed declaration
 * (`emp.EmpyreanRateIndexDeclarations`), one field per tab column; the tab's `_IX` rows are built
 * per client by the beta layer and are not stored. Rates are decimals (0.0325 = 3.25%).
 */
export interface EmpyreanRateIndexDeclaration {
  id: number;
  name: string;
  /** the key Empyrean joins on */
  mnemonic: string;
  groupId: string | null;
  description: string | null;
  ftpTableName: string | null;
  rateShockFloors: string | null;
  calculateGroup: number | null;
  /** `Single Rate`, `Not Linked`, ... */
  calcType: string;
  /** a declared curve (`SOFRCV`) or another index (`TgtFedFundsUL`) */
  linkedRate: string | null;
  pricingTermMths: number | null;
  pricingLagFactor: number | null;
  pricingLagType: string | null;
  rateAdd: number | null;
  absCap: number | null;
  absFloor: number | null;
  roundTo: number | null;
  dayCount: string | null;
  calendarRef: string | null;
  sortOrder: number;
}

/**
 * One row of the Empyrean `YieldCurves` tab (no space — not the `Yield Curves` market-data tab) —
 * PROD-649. Every client's file carries every active curve: since PROD-1640 that includes one FHLB
 * advance curve, `FHLBCV`, for every district.
 */
export interface EmpyreanYieldCurveDeclaration {
  id: number;
  name: string;
  mnemonic: string;
  groupId: string | null;
  shortEndAccrualMtd: string | null;
  shortEndTerm: number | null;
  accrualMtd: string | null;
  intCompoundFreq: string | null;
  interpolation: string | null;
  extrapTerm: number | null;
  spotInterpolation: string | null;
  ftpTableName: string | null;
  rateShockFloors: string | null;
  sortOrder: number;
}

/** Both declaration sets, active rows only, each in tab order. */
export interface EmpyreanRateDeclarations {
  rateIndices: EmpyreanRateIndexDeclaration[];
  yieldCurves: EmpyreanYieldCurveDeclaration[];
}
